import { useMutation, useQuery } from '@tanstack/react-query';
import apiClient from './client';
import { API } from './endpoints';
import { fetchReportStatuses, queueReportOnce } from './reportsApi';
import { useNotificationStore } from '../stores/notificationStore';
import { reportQueuedToast } from '../utils/reportQueuedToast';

// --- Types ---

export interface QueueReportRequest {
  reportType: string;
  fKey_User: number;
  fKey_RS_Report: number;
  settingsJson: string;
  reportName: string;
  dateFor: string;
}

export interface ReportResult {
  success: boolean;
  message: string;
  reportDocumentId: number | null;
  /** The same request was queued a moment ago (a double click): reportDocumentId is that report and nothing new was queued. */
  alreadyQueued?: boolean;
}

export interface ReportDocumentStatus {
  pKey_RS_ReportDocument: number;
  status: number; // REPORT_STATUS: 0=Pending, 1=Generating, 2=Complete, 3=Error, 4-9 see below
  statusMessage: string | null;
  guid: string | null;
}

/** `modReportUtils.ReportStatus` (modReportUtils.vb:8), the values tblRS_ReportDocument.Status takes. */
export const REPORT_STATUS = {
  PENDING: 0,
  PRINTING: 1,
  COMPLETED: 2,
  ERROR: 3,
  NO_DATA: 4,
  REQUIRED_INFORMATION: 5,
  INFORMATION: 6,
  INFORMATION_SELECT: 7,
  PRINTED: 8,
  CANCELED: 9,
} as const;

/**
 * True once the server is done with the document, whatever the outcome: only
 * Pending (0) and Printing (1) are still in progress. Everything from
 * Completed (2) up - error, no data, canceled and the rest - will not change
 * again, so polling for it has to stop.
 */
export function isReportStatusFinal(status: number | null | undefined): boolean {
  return typeof status === 'number' && status >= REPORT_STATUS.COMPLETED;
}

/** A finished document there is something to open: Completed, or Completed and since viewed or printed (both 8). */
export function reportStatusHasDocument(status: number | null | undefined): boolean {
  return status === REPORT_STATUS.COMPLETED || status === REPORT_STATUS.PRINTED;
}

// --- Report Type IDs ---
// NOTE: IDs below are placeholders. TRAY_TICKET (1) is confirmed.
// MEAL_PATTERN, ADJUSTMENT, HISTORY, and NUTRITIONAL_ANALYSIS IDs need to be
// verified against the tblRS_Report table in the database before use with the
// server-side report queue. The Nutritional Analysis page currently uses
// client-side CSV export and does not depend on these IDs.
export const REPORT_TYPES = {
  TRAY_TICKET: 1,
  MEAL_PATTERN: 45, // Unverified - check tblRS_Report
  ADJUSTMENT: 46, // Unverified - check tblRS_Report
  HISTORY: 47, // Unverified - check tblRS_Report
  NUTRITIONAL_ANALYSIS: 48, // Unverified - check tblRS_Report
} as const;

// --- Mutations ---

export function useQueueReport(facilityId: number) {
  const { addNotification } = useNotificationStore();

  return useMutation({
    // "Queuing" from the click, and one request for repeat clicks (reportsApi.queueReportOnce).
    mutationFn: (request: QueueReportRequest) => queueReportOnce(facilityId, request),
    onSuccess: (result) => {
      if (result.success) {
        // "View PDF" here did nothing (the report is not ready yet); the Print Queue is where it appears.
        addNotification(reportQueuedToast(result.alreadyQueued ? 'This report was already queued a moment ago' : 'Report queued'));
      } else {
        addNotification(`Report failed: ${result.message}`, 'error');
      }
    },
    onError: () => {
      addNotification('Failed to queue report', 'error');
    },
  });
}

// --- Tray Ticket shortcut ---

export function useQueueTrayTicket(facilityId: number) {
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (settings: {
      fKey_User: number;
      reportDate: string;
      mealtimes: number[];
      patients: number[];
    }) => {
      const { data } = await apiClient.post<ReportResult>(
        API.reportQueueSpecific(facilityId, 'TrayTicket'),
        {
          fKey_Facility: facilityId,
          fKey_User: settings.fKey_User,
          fKey_RS_Report: REPORT_TYPES.TRAY_TICKET,
          reportDate: settings.reportDate,
          mealtimes: settings.mealtimes,
          patients: settings.patients,
          printByPatient: true,
          includeSelective: true,
          includeAllergies: true,
          includeConsistency: true,
          includeDietType: true,
          includeRoom: true,
          ticketStyle: 1,
        },
      );
      return data;
    },
    onSuccess: (result) => {
      if (result.success) {
        addNotification(reportQueuedToast('Tray ticket queued for printing'));
      } else {
        addNotification(`Tray ticket failed: ${result.message}`, 'error');
      }
    },
    onError: () => {
      addNotification('Failed to queue tray ticket', 'error');
    },
  });
}

// --- Report Status Polling ---

export function useReportStatus(
  facilityId: number,
  reportDocumentId: number | null,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ['reportStatus', facilityId, reportDocumentId],
    queryFn: async () => {
      // One primary-key lookup (POST api/report/Status), as the auto-opener
      // does. This used to pull every document in the facility
      // (GetReportDocuments with viewAll) every 3 s to read one row, and the
      // API refuses View All below Report level 3, so for those users the
      // page never saw its report finish. (enabled guards the null id.)
      const data: ReportDocumentStatus[] = await fetchReportStatuses(facilityId, [reportDocumentId as number]);
      return data.find(
        (d) => d.pKey_RS_ReportDocument === reportDocumentId,
      ) ?? null;
    },
    enabled: enabled && !!reportDocumentId && facilityId > 0,
    refetchInterval: (query) => {
      // Stop polling once the document is final - complete, error, no data,
      // canceled, ... (2-9). Stopping only at 2 or 3 polled a no-data or
      // canceled document every 3 seconds for as long as the page was open.
      if (isReportStatusFinal(query.state.data?.status)) return false;
      return 3000; // Poll every 3 seconds while pending/generating
    },
  });
}
